import { z } from 'zod';

import {
  doriisImportParseStatusValues,
  doriisOfficialSystemValues,
  doriisOperationalDocumentTypeValues,
  doriisRawImportRowStatusValues,
  doriisReportAttachmentStatusValues,
  doriisReportCompletenessStatusValues,
  doriisReportConflictStatusValues,
  doriisReportLinkedFilterValues,
  doriisReportKindValues,
  doriisReportLinkRoleValues,
  doriisReportLinkStatusValues,
  doriisSnapshotTypeValues,
  doriisSourceKindValues,
  type AddDoriisReportEvidenceSnapshotRequest,
  type AddDoriisReportEvidenceSnapshotResponse,
  type AddDoriisCoverReportEvidenceRequest,
  type AddDoriisCoverReportEvidenceResponse,
  type CreateManualDoriisReportStubRequest,
  type CreateManualDoriisReportStubResponse,
  type DoriisCoverAttachmentInboundSuggestionDto,
  type DoriisCoverAttachmentRowInput,
  type DoriisFlatImportRowInput,
  type DoriisImportBatchDto,
  type DoriisImportParseStatus,
  type DoriisMatchingSignalsDto,
  type DoriisOfficialFieldsDto,
  type DoriisOfficialSystem,
  type DoriisOperationalDocumentType,
  type DoriisRawImportRowDto,
  type DoriisRawImportRowStatus,
  type DoriisReportAttachmentDto,
  type DoriisReportAttachmentStatus,
  type DoriisReportCompletenessStatus,
  type DoriisReportConflictStatus,
  type DoriisReportDetailDto,
  type DoriisReportEvidenceSnapshotDto,
  type DoriisReportKind,
  type DoriisReportLinkDto,
  type DoriisReportLinkedFilter,
  type DoriisReportLinkRole,
  type DoriisReportLinkStatus,
  type DoriisShippingReportDto,
  type DoriisSnapshotType,
  type DoriisSourceKind,
  type DoriisReportDocumentSuggestionDto,
  type DoriisSuggestedMatchDto,
  type GetActiveDoriisReportLinkRequest,
  type GetActiveDoriisReportLinkResponse,
  type GetDoriisCoverAttachmentInboundSuggestionsRequest,
  type GetDoriisCoverAttachmentInboundSuggestionsResponse,
  type GetDoriisReportRequest,
  type GetDoriisReportDocumentSuggestionsRequest,
  type GetDoriisReportDocumentSuggestionsResponse,
  type GetDoriisReportResponse,
  type GetDoriisSuggestedMatchesRequest,
  type GetDoriisSuggestedMatchesResponse,
  type ImportFlatDoriisRowsRequest,
  type ImportFlatDoriisRowsResponse,
  type ListDoriisRawImportRowsRequest,
  type ListDoriisRawImportRowsResponse,
  type LinkDoriisReportRequest,
  type LinkDoriisReportResponse,
  type ListDoriisReportsRequest,
  type ListDoriisReportsResponse,
  type UnlinkDoriisReportRequest,
  type UnlinkDoriisReportResponse,
} from './doriis-reports.dto.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const isoDateSchema = z.iso.date();
const isoDateTimeSchema = z.iso.datetime();
const trimmedStringSchema = z.string().trim().min(1);
const optionalTrimmedStringSchema = z.string().trim().nullable().optional();
const jsonObjectSchema = z.record(z.string(), z.unknown());
const nonNegativeIntegerSchema = z.number().int().min(0);

export const DoriisOfficialSystemSchema: z.ZodType<DoriisOfficialSystem> = z.enum(
  doriisOfficialSystemValues,
);
export const DoriisReportKindSchema: z.ZodType<DoriisReportKind> = z.enum(doriisReportKindValues);
export const DoriisSnapshotTypeSchema: z.ZodType<DoriisSnapshotType> =
  z.enum(doriisSnapshotTypeValues);
export const DoriisSourceKindSchema: z.ZodType<DoriisSourceKind> = z.enum(doriisSourceKindValues);
export const DoriisReportCompletenessStatusSchema: z.ZodType<DoriisReportCompletenessStatus> =
  z.enum(doriisReportCompletenessStatusValues);
export const DoriisReportConflictStatusSchema: z.ZodType<DoriisReportConflictStatus> = z.enum(
  doriisReportConflictStatusValues,
);
export const DoriisImportParseStatusSchema: z.ZodType<DoriisImportParseStatus> = z.enum(
  doriisImportParseStatusValues,
);
export const DoriisRawImportRowStatusSchema: z.ZodType<DoriisRawImportRowStatus> = z.enum(
  doriisRawImportRowStatusValues,
);
export const DoriisReportAttachmentStatusSchema: z.ZodType<DoriisReportAttachmentStatus> = z.enum(
  doriisReportAttachmentStatusValues,
);
export const DoriisReportLinkRoleSchema: z.ZodType<DoriisReportLinkRole> = z.enum(
  doriisReportLinkRoleValues,
);
export const DoriisOperationalDocumentTypeSchema: z.ZodType<DoriisOperationalDocumentType> = z.enum(
  doriisOperationalDocumentTypeValues,
);
export const DoriisReportLinkStatusSchema: z.ZodType<DoriisReportLinkStatus> = z.enum(
  doriisReportLinkStatusValues,
);
export const DoriisReportLinkedFilterSchema: z.ZodType<DoriisReportLinkedFilter> = z.enum(
  doriisReportLinkedFilterValues,
);

export const DoriisFlatImportRowInputSchema: z.ZodType<DoriisFlatImportRowInput> = z
  .object({
    rowNumber: z.number().int().min(1).nullable().optional(),
    values: jsonObjectSchema,
  })
  .strict();

export const DoriisOfficialFieldsSchema: z.ZodType<DoriisOfficialFieldsDto> = z
  .object({
    shippingReportNumber: optionalTrimmedStringSchema,
    materialType: optionalTrimmedStringSchema,
    shipperDoriisId: optionalTrimmedStringSchema,
    shipperName: optionalTrimmedStringSchema,
    receiverDoriisId: optionalTrimmedStringSchema,
    receiverName: optionalTrimmedStringSchema,
    reportFromDate: isoDateSchema.nullable().optional(),
    reportThroughDate: isoDateSchema.nullable().optional(),
    shippedRedemptionWeightLb: ReportableWeightLbSchema.nullable().optional(),
    shippedTotalRefundValueCents: nonNegativeIntegerSchema.nullable().optional(),
    receivedDate: isoDateSchema.nullable().optional(),
    weightTicketNumber: optionalTrimmedStringSchema,
    receivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    processingPaymentCents: nonNegativeIntegerSchema.nullable().optional(),
    refundValueCents: nonNegativeIntegerSchema.nullable().optional(),
    adminFeeCents: nonNegativeIntegerSchema.nullable().optional(),
    totalPaymentCents: nonNegativeIntegerSchema.nullable().optional(),
    fsn: optionalTrimmedStringSchema,
    attachedToFsn: optionalTrimmedStringSchema,
    coverOwnBuybackWeightLb: ReportableWeightLbSchema.nullable().optional(),
    coverOwnBuybackValueCents: nonNegativeIntegerSchema.nullable().optional(),
    coverOwnRlWeightLb: ReportableWeightLbSchema.nullable().optional(),
    coverOwnRlValueCents: nonNegativeIntegerSchema.nullable().optional(),
    coverOfficialTotalReceivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    coverOfficialTotalPaymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    coverOfficialTotalRedemptionWeightLb: ReportableWeightLbSchema.nullable().optional(),
    coverOfficialTotalPaymentValueCents: nonNegativeIntegerSchema.nullable().optional(),
  })
  .strict();

export const DoriisMatchingSignalsSchema: z.ZodType<DoriisMatchingSignalsDto> = z
  .object({
    shippingReportNumberKey: optionalTrimmedStringSchema,
    materialType: optionalTrimmedStringSchema,
    shipperDoriisId: optionalTrimmedStringSchema,
    receiverDoriisId: optionalTrimmedStringSchema,
    reportFromDate: isoDateSchema.nullable().optional(),
    reportThroughDate: isoDateSchema.nullable().optional(),
    receivedDate: isoDateSchema.nullable().optional(),
    weightTicketNumber: optionalTrimmedStringSchema,
    shippedRedemptionWeightLb: ReportableWeightLbSchema.nullable().optional(),
    receivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    refundValueCents: nonNegativeIntegerSchema.nullable().optional(),
    totalPaymentCents: nonNegativeIntegerSchema.nullable().optional(),
  })
  .strict();

export const DoriisShippingReportSchema: z.ZodType<DoriisShippingReportDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    officialSystem: DoriisOfficialSystemSchema,
    shippingReportNumber: trimmedStringSchema.nullable(),
    shippingReportNumberKey: trimmedStringSchema.nullable(),
    reportKind: DoriisReportKindSchema,
    shipperDoriisId: trimmedStringSchema.nullable(),
    shipperName: trimmedStringSchema.nullable(),
    receiverDoriisId: trimmedStringSchema.nullable(),
    receiverName: trimmedStringSchema.nullable(),
    shipperRcScopeType: trimmedStringSchema.nullable(),
    shipperRcScopeId: z.uuid().nullable(),
    receiverRcScopeType: trimmedStringSchema.nullable(),
    receiverRcScopeId: z.uuid().nullable(),
    materialType: trimmedStringSchema.nullable(),
    reportFromDate: isoDateSchema.nullable(),
    reportThroughDate: isoDateSchema.nullable(),
    shippedRedemptionWeightLb: ReportableWeightLbSchema.nullable(),
    shippedTotalRefundValueCents: nonNegativeIntegerSchema.nullable(),
    receivedDate: isoDateSchema.nullable(),
    weightTicketNumber: trimmedStringSchema.nullable(),
    receivedWeightLb: ReportableWeightLbSchema.nullable(),
    paymentWeightLb: ReportableWeightLbSchema.nullable(),
    processingPaymentCents: nonNegativeIntegerSchema.nullable(),
    refundValueCents: nonNegativeIntegerSchema.nullable(),
    adminFeeCents: nonNegativeIntegerSchema.nullable(),
    totalPaymentCents: nonNegativeIntegerSchema.nullable(),
    completenessStatus: DoriisReportCompletenessStatusSchema,
    conflictStatus: DoriisReportConflictStatusSchema,
    latestEvidenceAt: isoDateTimeSchema.nullable(),
    latestFlatExportEvidenceAt: isoDateTimeSchema.nullable(),
    latestDetailEvidenceAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisImportBatchSchema: z.ZodType<DoriisImportBatchDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    sourceKind: DoriisSourceKindSchema,
    sourceLabel: trimmedStringSchema.nullable(),
    fileName: trimmedStringSchema.nullable(),
    importedByUserId: z.uuid().nullable(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    importedAt: isoDateTimeSchema,
    totalRowCount: nonNegativeIntegerSchema,
    importedRowCount: nonNegativeIntegerSchema,
    missingReportNumberCount: nonNegativeIntegerSchema,
    duplicateRowCount: nonNegativeIntegerSchema,
    conflictRowCount: nonNegativeIntegerSchema,
    parseStatus: DoriisImportParseStatusSchema,
    createdAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisRawImportRowSchema: z.ZodType<DoriisRawImportRowDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    batchId: z.uuid(),
    reportId: z.uuid().nullable(),
    evidenceSnapshotId: z.uuid().nullable(),
    originalRowNumber: z.number().int().min(1),
    normalizedRowHash: trimmedStringSchema,
    shippingReportNumber: trimmedStringSchema.nullable(),
    shippingReportNumberKey: trimmedStringSchema.nullable(),
    parsedOfficialFields: DoriisOfficialFieldsSchema,
    commercialFields: jsonObjectSchema.nullable(),
    rawRow: jsonObjectSchema,
    rowStatus: DoriisRawImportRowStatusSchema,
    conflictFields: z.array(trimmedStringSchema),
    createdAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisReportEvidenceSnapshotSchema: z.ZodType<DoriisReportEvidenceSnapshotDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    reportId: z.uuid(),
    snapshotType: DoriisSnapshotTypeSchema,
    sourceKind: DoriisSourceKindSchema,
    parsedOfficialFields: DoriisOfficialFieldsSchema,
    rawEvidence: jsonObjectSchema,
    importBatchId: z.uuid().nullable(),
    rawImportRowId: z.uuid().nullable(),
    actorUserId: z.uuid().nullable(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    capturedAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisReportAttachmentSchema: z.ZodType<DoriisReportAttachmentDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    coverReportId: z.uuid(),
    childReportNumber: trimmedStringSchema.nullable(),
    childReportNumberKey: trimmedStringSchema.nullable(),
    childReportId: z.uuid().nullable(),
    sourceSnapshotId: z.uuid().nullable(),
    attachmentValues: jsonObjectSchema,
    status: DoriisReportAttachmentStatusSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisReportLinkSchema: z.ZodType<DoriisReportLinkDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    reportId: z.uuid(),
    coverAttachmentId: z.uuid().nullable(),
    role: DoriisReportLinkRoleSchema,
    documentType: DoriisOperationalDocumentTypeSchema,
    documentId: z.uuid(),
    status: DoriisReportLinkStatusSchema,
    reason: trimmedStringSchema.nullable(),
    linkedByUserId: z.uuid().nullable(),
    unlinkedByUserId: z.uuid().nullable(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    linkedAt: isoDateTimeSchema,
    unlinkedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();

export const DoriisReportDetailSchema: z.ZodType<DoriisReportDetailDto> = z
  .object({
    report: DoriisShippingReportSchema,
    evidenceSnapshots: z.array(DoriisReportEvidenceSnapshotSchema),
    attachments: z.array(DoriisReportAttachmentSchema),
    links: z.array(DoriisReportLinkSchema),
  })
  .strict();

export const ImportFlatDoriisRowsRequestSchema: z.ZodType<ImportFlatDoriisRowsRequest> = z
  .object({
    companyId: z.uuid(),
    sourceLabel: trimmedStringSchema.nullable().optional(),
    fileName: trimmedStringSchema.nullable().optional(),
    importedAt: isoDateTimeSchema.nullable().optional(),
    rows: z.array(DoriisFlatImportRowInputSchema).min(1),
  })
  .strict();

export const ImportFlatDoriisRowsResponseSchema: z.ZodType<ImportFlatDoriisRowsResponse> = z
  .object({
    batch: DoriisImportBatchSchema,
    rows: z.array(DoriisRawImportRowSchema),
    reports: z.array(DoriisShippingReportSchema),
  })
  .strict();

export const CreateManualDoriisReportStubRequestSchema: z.ZodType<CreateManualDoriisReportStubRequest> =
  z
    .object({
      companyId: z.uuid(),
      shippingReportNumber: trimmedStringSchema,
      reportKind: DoriisReportKindSchema.optional(),
      rawEvidence: jsonObjectSchema.nullable().optional(),
    })
    .strict();

export const CreateManualDoriisReportStubResponseSchema: z.ZodType<CreateManualDoriisReportStubResponse> =
  z
    .object({
      report: DoriisShippingReportSchema,
      evidenceSnapshot: DoriisReportEvidenceSnapshotSchema,
    })
    .strict();

export const AddDoriisReportEvidenceSnapshotRequestSchema: z.ZodType<AddDoriisReportEvidenceSnapshotRequest> =
  z
    .object({
      companyId: z.uuid(),
      reportId: z.uuid(),
      snapshotType: DoriisSnapshotTypeSchema,
      sourceKind: DoriisSourceKindSchema,
      parsedOfficialFields: DoriisOfficialFieldsSchema.optional(),
      rawEvidence: jsonObjectSchema.nullable().optional(),
      capturedAt: isoDateTimeSchema.nullable().optional(),
    })
    .strict();

export const AddDoriisReportEvidenceSnapshotResponseSchema: z.ZodType<AddDoriisReportEvidenceSnapshotResponse> =
  z
    .object({
      report: DoriisShippingReportSchema,
      evidenceSnapshot: DoriisReportEvidenceSnapshotSchema,
    })
    .strict();

export const DoriisCoverAttachmentRowInputSchema: z.ZodType<DoriisCoverAttachmentRowInput> = z
  .object({
    childReportNumber: optionalTrimmedStringSchema,
    shipperDoriisId: optionalTrimmedStringSchema,
    shipperName: optionalTrimmedStringSchema,
    receiverDoriisId: optionalTrimmedStringSchema,
    receiverName: optionalTrimmedStringSchema,
    reportFromDate: isoDateSchema.nullable().optional(),
    reportThroughDate: isoDateSchema.nullable().optional(),
    receivedDate: isoDateSchema.nullable().optional(),
    receivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
    totalRedemptionWeightLb: ReportableWeightLbSchema.nullable().optional(),
    totalRefundPaymentValueCents: nonNegativeIntegerSchema.nullable().optional(),
    weightTicketNumber: optionalTrimmedStringSchema,
    rawEvidence: jsonObjectSchema.nullable().optional(),
  })
  .strict();

export const AddDoriisCoverReportEvidenceRequestSchema: z.ZodType<AddDoriisCoverReportEvidenceRequest> =
  z
    .object({
      companyId: z.uuid(),
      reportId: z.uuid(),
      sourceKind: z.enum(['MANUAL_ENTRY', 'DORIIS_DETAIL_PRINTABLE', 'DORIIS_DETAIL_SCREEN']),
      parsedOfficialFields: DoriisOfficialFieldsSchema.optional(),
      attachmentRows: z.array(DoriisCoverAttachmentRowInputSchema).optional(),
      rawEvidence: jsonObjectSchema.nullable().optional(),
      capturedAt: isoDateTimeSchema.nullable().optional(),
    })
    .strict();

export const AddDoriisCoverReportEvidenceResponseSchema: z.ZodType<AddDoriisCoverReportEvidenceResponse> =
  z
    .object({
      report: DoriisShippingReportSchema,
      evidenceSnapshot: DoriisReportEvidenceSnapshotSchema,
      attachments: z.array(DoriisReportAttachmentSchema),
    })
    .strict();

export const ListDoriisReportsRequestSchema: z.ZodType<ListDoriisReportsRequest> = z
  .object({
    companyId: z.uuid(),
    query: z.string().trim().nullable().optional(),
    reportKind: DoriisReportKindSchema.nullable().optional(),
    conflictStatus: DoriisReportConflictStatusSchema.nullable().optional(),
    completenessStatus: DoriisReportCompletenessStatusSchema.nullable().optional(),
    linkedStatus: DoriisReportLinkedFilterSchema.nullable().optional(),
    limit: z.number().int().min(1).max(100).nullable().optional(),
  })
  .strict();

export const ListDoriisReportsResponseSchema: z.ZodType<ListDoriisReportsResponse> = z
  .object({
    items: z.array(DoriisShippingReportSchema),
  })
  .strict();

export const GetDoriisReportRequestSchema: z.ZodType<GetDoriisReportRequest> = z
  .object({
    companyId: z.uuid(),
    reportId: z.uuid(),
  })
  .strict();

export const GetDoriisReportResponseSchema: z.ZodType<GetDoriisReportResponse> = z
  .object({
    detail: DoriisReportDetailSchema,
  })
  .strict();

export const ListDoriisRawImportRowsRequestSchema: z.ZodType<ListDoriisRawImportRowsRequest> = z
  .object({
    companyId: z.uuid(),
    query: z.string().trim().nullable().optional(),
    rowStatus: DoriisRawImportRowStatusSchema.nullable().optional(),
    rowStatuses: z.array(DoriisRawImportRowStatusSchema).min(1).max(4).nullable().optional(),
    reportId: z.uuid().nullable().optional(),
    limit: z.number().int().min(1).max(100).nullable().optional(),
  })
  .strict();

export const ListDoriisRawImportRowsResponseSchema: z.ZodType<ListDoriisRawImportRowsResponse> = z
  .object({
    items: z.array(DoriisRawImportRowSchema),
  })
  .strict();

export const GetActiveDoriisReportLinkRequestSchema: z.ZodType<GetActiveDoriisReportLinkRequest> = z
  .object({
    companyId: z.uuid(),
    documentType: DoriisOperationalDocumentTypeSchema,
    documentId: z.uuid(),
    role: DoriisReportLinkRoleSchema,
  })
  .strict();

export const GetActiveDoriisReportLinkResponseSchema: z.ZodType<GetActiveDoriisReportLinkResponse> =
  z
    .object({
      link: DoriisReportLinkSchema.nullable(),
      detail: DoriisReportDetailSchema.nullable(),
    })
    .strict();

export const GetDoriisSuggestedMatchesRequestSchema: z.ZodType<GetDoriisSuggestedMatchesRequest> = z
  .object({
    companyId: z.uuid(),
    documentType: DoriisOperationalDocumentTypeSchema,
    documentId: z.uuid(),
    role: DoriisReportLinkRoleSchema.nullable().optional(),
    shippingReportNumber: z.string().trim().nullable().optional(),
    materialType: z.string().trim().nullable().optional(),
    shipperDoriisId: z.string().trim().nullable().optional(),
    receiverDoriisId: z.string().trim().nullable().optional(),
    receivedWeightLb: ReportableWeightLbSchema.nullable().optional(),
    paymentWeightLb: ReportableWeightLbSchema.nullable().optional(),
  })
  .strict();

export const DoriisSuggestedMatchSchema: z.ZodType<DoriisSuggestedMatchDto> = z
  .object({
    report: DoriisShippingReportSchema,
    score: z.number().min(0).max(100),
    reasons: z.array(trimmedStringSchema),
  })
  .strict();

export const GetDoriisSuggestedMatchesResponseSchema: z.ZodType<GetDoriisSuggestedMatchesResponse> =
  z
    .object({
      items: z.array(DoriisSuggestedMatchSchema),
    })
    .strict();

export const GetDoriisReportDocumentSuggestionsRequestSchema: z.ZodType<GetDoriisReportDocumentSuggestionsRequest> =
  z
    .object({
      companyId: z.uuid(),
      reportId: z.uuid(),
      limit: z.number().int().min(1).max(25).nullable().optional(),
    })
    .strict();

export const DoriisReportDocumentSuggestionSchema: z.ZodType<DoriisReportDocumentSuggestionDto> = z
  .object({
    documentType: z.enum(['Shipment', 'InboundReceipt']),
    documentId: z.uuid(),
    role: z.enum(['SHIPPER_SIDE', 'RECEIVER_SIDE']),
    score: z.number().min(0).max(100),
    reasons: z.array(trimmedStringSchema),
    label: trimmedStringSchema,
    materialLabel: trimmedStringSchema.nullable(),
    dateLabel: trimmedStringSchema.nullable(),
    weightLabel: trimmedStringSchema.nullable(),
    counterpartyLabel: trimmedStringSchema.nullable(),
    alreadyLinked: z.boolean(),
  })
  .strict();

export const GetDoriisReportDocumentSuggestionsResponseSchema: z.ZodType<GetDoriisReportDocumentSuggestionsResponse> =
  z
    .object({
      items: z.array(DoriisReportDocumentSuggestionSchema),
    })
    .strict();

export const GetDoriisCoverAttachmentInboundSuggestionsRequestSchema: z.ZodType<GetDoriisCoverAttachmentInboundSuggestionsRequest> =
  z
    .object({
      companyId: z.uuid(),
      coverAttachmentId: z.uuid(),
      limit: z.number().int().min(1).max(25).nullable().optional(),
    })
    .strict();

export const DoriisCoverAttachmentInboundSuggestionSchema: z.ZodType<DoriisCoverAttachmentInboundSuggestionDto> =
  z
    .object({
      documentType: z.literal('InboundReceipt'),
      documentId: z.uuid(),
      role: z.literal('COVER_ATTACHMENT'),
      coverAttachmentId: z.uuid(),
      score: z.number().min(0).max(100),
      reasons: z.array(trimmedStringSchema),
      label: trimmedStringSchema,
      materialLabel: trimmedStringSchema.nullable(),
      dateLabel: trimmedStringSchema.nullable(),
      weightLabel: trimmedStringSchema.nullable(),
      counterpartyLabel: trimmedStringSchema.nullable(),
      alreadyLinked: z.boolean(),
    })
    .strict();

export const GetDoriisCoverAttachmentInboundSuggestionsResponseSchema: z.ZodType<GetDoriisCoverAttachmentInboundSuggestionsResponse> =
  z
    .object({
      items: z.array(DoriisCoverAttachmentInboundSuggestionSchema),
    })
    .strict();

export const LinkDoriisReportRequestSchema: z.ZodType<LinkDoriisReportRequest> = z
  .object({
    companyId: z.uuid(),
    reportId: z.uuid(),
    role: DoriisReportLinkRoleSchema,
    documentType: DoriisOperationalDocumentTypeSchema,
    documentId: z.uuid(),
    coverAttachmentId: z.uuid().nullable().optional(),
    reason: z.string().trim().nullable().optional(),
  })
  .strict();

export const LinkDoriisReportResponseSchema: z.ZodType<LinkDoriisReportResponse> = z
  .object({
    link: DoriisReportLinkSchema,
  })
  .strict();

export const UnlinkDoriisReportRequestSchema: z.ZodType<UnlinkDoriisReportRequest> = z
  .object({
    companyId: z.uuid(),
    linkId: z.uuid(),
    reason: trimmedStringSchema,
  })
  .strict();

export const UnlinkDoriisReportResponseSchema: z.ZodType<UnlinkDoriisReportResponse> = z
  .object({
    link: DoriisReportLinkSchema,
  })
  .strict();
